import { ROLE_IDS_TO_NAMES } from "$global-providers/user-provider/role-definitions";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import React, { useEffect, useMemo } from "react";
import type { JwtUser, UserContextUser } from "./types";
export const AUTH0_ELEPHANT_CLAIM_NAMESPACE = "https://elephantprimarycare.com";

export const UserContext = React.createContext<{
  user: UserContextUser | null;
}>({ user: null });

/**
 * This should only be used in situations where the user may not be logged in, most
 * likely using useUser would be preferred.
 * @returns The logged in user, or null if the user is not logged in.
 */
export const useLazyUser = (): UserContextUser | null => {
  const context = React.useContext(UserContext);
  return context?.user ?? null;
};

/**
 * This is a syntactic sugar function as it will return you a non-null user, skipping
 * the need to perform a null check throughout our codebase.
 * It must only be used under routes that have been wrapped in a <RequireAuth>.
 * @returns The logged in user.
 */
export function useUser(): UserContextUser {
  const user = useLazyUser();

  if (!user) {
    throw new Error("useUser must be used under a <RequireAuth> route.");
  }

  return user;
}

export const UserProvider: FCwC = ({ children }) => {
  const { user: auth0User } = useAuth0();

  const user = useMemo<UserContextUser | null>(() => {
    if (!auth0User) return null;

    // This is a DX cast - we don't do any checks that the user has the correct fields,
    // instead just trust our JWT to be correct as configured in the Auth0 hooks.
    const elephantUser = auth0User[AUTH0_ELEPHANT_CLAIM_NAMESPACE] as JwtUser;
    const roleId = elephantUser.role?._id;
    return {
      id: elephantUser.id,
      roleId,
      email: elephantUser.email,
      facilityId: elephantUser.clinic,
      organisationId: elephantUser.organisation,
      firstName: elephantUser.firstName,
      middleName: elephantUser.middleName,
      lastName: elephantUser.lastName,
      language: elephantUser.language,
      active: true, // Assume user is active as they just had a JWT issued
      role: roleId ? ROLE_IDS_TO_NAMES[roleId] : undefined,
    };
  }, [auth0User?.[AUTH0_ELEPHANT_CLAIM_NAMESPACE]]);

  useEffect(() => {
    if (!user) return;

    Sentry.setUser({ id: user.id, ip_address: "{{auto}}" });
    Sentry.setTag("facilityId", user.facilityId);
    Sentry.setTag("organisationId", user.organisationId);
  }, [user]);

  return (
    <UserContext.Provider value={{ user }}>{children}</UserContext.Provider>
  );
};
